import { basename, extname } from 'path';
import { existsSync } from 'fs';
import { pathToFileURL } from 'url';
import { OkraRuntimeError } from '../errors.js';
import {
  copySourceIntoStoreEffect,
  createLocalDocumentIdEffect,
  ensureLocalStoreEffect,
  initializeDocumentDirEffect,
  LocalStoreError,
  readDocumentPageTextEffect,
  readDocumentRecordEffect,
  resolveLocalDataDir,
  writeDocumentPageTextEffect,
  writeDocumentRecordEffect,
} from './store.js';
import { extractLocalDocumentEffect, LocalExtractError, localToolReportEffect } from './extract.js';
import type {
  LocalCitation,
  LocalDoctorReport,
  LocalDocumentRecord,
  LocalTableCandidate,
} from './types.js';
import { Data, Effect } from 'effect';

type LocalDocumentOperation =
  | 'ingest'
  | 'status'
  | 'summary'
  | 'search'
  | 'page'
  | 'tables'
  | 'doctor';

export class LocalDocumentHarnessError extends Data.TaggedError('LocalDocumentHarnessError')<{
  readonly operation: LocalDocumentOperation;
  readonly status?: number;
  readonly cause: unknown;
}> {}

function localDocumentFailure(
  operation: LocalDocumentOperation,
  error: unknown,
): LocalDocumentHarnessError {
  const cause =
    error instanceof LocalStoreError || error instanceof LocalExtractError ? error.cause : error;
  return new LocalDocumentHarnessError({
    operation,
    ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
    cause,
  });
}

function localDocumentBoundary<A>(
  operation: LocalDocumentOperation,
  run: () => A,
): Effect.Effect<A, LocalDocumentHarnessError> {
  return Effect.try({
    try: run,
    catch: (cause) => localDocumentFailure(operation, cause),
  });
}

function mapLocalDocumentError<A, E>(
  operation: LocalDocumentOperation,
  program: Effect.Effect<A, E>,
): Effect.Effect<A, LocalDocumentHarnessError> {
  return program.pipe(Effect.mapError((error) => localDocumentFailure(operation, error)));
}

function runLocalDocumentEffect<A>(program: Effect.Effect<A, LocalDocumentHarnessError>): A {
  return Effect.runSync(program.pipe(Effect.catch((error) => Effect.fail(error.cause))));
}

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'how',
  'in',
  'is',
  'it',
  'of',
  'on',
  'or',
  'that',
  'the',
  'this',
  'to',
  'what',
  'when',
  'where',
  'who',
  'why',
  'with',
]);

function sanitizeExcerpt(text: string, maxLength = 280): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 1).trimEnd()}…`
    : normalized;
}

function sanitizeTablePreview(text: string, maxLength = 320): string {
  const normalized = text
    .replace(/\r/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 1).trimEnd()}…`
    : normalized;
}

function tokenizeQuery(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/g)
    .map((token) => token.trim())
    .filter((token) => token.length >= 2 && !STOP_WORDS.has(token));
}

function buildDocumentInfo(record: LocalDocumentRecord) {
  return {
    documentId: record.documentId,
    filename: record.filename,
    status: record.status,
    pageCount: record.pageCount,
    charCount: record.charCount,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    error: record.error,
    extractor: record.extractor,
  };
}

function ensureReadyEffect(
  operation: LocalDocumentOperation,
  documentId: string,
  dataDir?: string,
): Effect.Effect<LocalDocumentRecord, LocalDocumentHarnessError> {
  return mapLocalDocumentError(operation, readDocumentRecordEffect(documentId, dataDir)).pipe(
    Effect.flatMap((record) =>
      record.status === 'failed'
        ? Effect.fail(
            localDocumentFailure(
              operation,
              new OkraRuntimeError(
                'INVALID_REQUEST',
                record.error || `Local document ${documentId} failed to process`,
                500,
              ),
            ),
          )
        : Effect.succeed(record),
    ),
  );
}

function snippetAround(text: string, tokens: string[]): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  const lower = normalized.toLowerCase();
  let index = -1;
  for (const token of tokens) {
    index = lower.indexOf(token);
    if (index >= 0) break;
  }
  if (index < 0) {
    return sanitizeExcerpt(normalized);
  }
  const start = Math.max(0, index - 110);
  const end = Math.min(normalized.length, index + 170);
  return sanitizeExcerpt(normalized.slice(start, end));
}

function summarizeTextEffect(
  record: LocalDocumentRecord,
  dataDir?: string,
): Effect.Effect<{ summary: string; citations: LocalCitation[] }, LocalDocumentHarnessError> {
  return Effect.gen(function* () {
    const citations: LocalCitation[] = [];
    const seenPages = new Set<number>();
    const snippets: string[] = [];

    for (const page of record.pages) {
      const text = yield* mapLocalDocumentError(
        'summary',
        readDocumentPageTextEffect(record.documentId, page.pageNumber, dataDir),
      );
      const blocks = text
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean);

      for (const block of blocks) {
        snippets.push(block);
        if (!seenPages.has(page.pageNumber)) {
          citations.push({
            type: 'page_location',
            cited_text: sanitizeExcerpt(block, 200),
            start_page_number: page.pageNumber,
            end_page_number: page.pageNumber,
            // Proof = the stored source PDF at the cited page (file://…#page=N) —
            // the self-host equivalent of the cloud's res.okrapdf.com page image.
            citation_url: `${pathToFileURL(record.storedPath).href}#page=${page.pageNumber}`,
            // Summary snippets are approximate (first block per page), not a
            // grounded exact span — so the honest match grade is 'fuzzy'.
            match: 'fuzzy',
          });
          seenPages.add(page.pageNumber);
        }
        if (snippets.join(' ').length >= 420 || citations.length >= 3) {
          break;
        }
      }
      if (snippets.join(' ').length >= 420 || citations.length >= 3) break;
    }

    const summary = snippets.length
      ? sanitizeExcerpt(snippets.join(' '), 520)
      : `No extractable text was found in ${record.filename}.`;

    return { summary, citations };
  });
}

export function ingestLocalDocumentEffect(sourcePath: string, dataDir?: string) {
  return Effect.gen(function* () {
    yield* localDocumentBoundary('ingest', () => {
      if (!existsSync(sourcePath)) {
        throw new OkraRuntimeError('DOCUMENT_NOT_FOUND', `File not found: ${sourcePath}`, 404);
      }
      if (extname(sourcePath).toLowerCase() !== '.pdf') {
        throw new OkraRuntimeError(
          'INVALID_REQUEST',
          `Local ingest only supports PDFs: ${sourcePath}`,
          400,
        );
      }
    });

    const resolvedDataDir = yield* mapLocalDocumentError('ingest', ensureLocalStoreEffect(dataDir));
    const documentId = yield* mapLocalDocumentError('ingest', createLocalDocumentIdEffect());
    yield* mapLocalDocumentError(
      'ingest',
      initializeDocumentDirEffect(documentId, resolvedDataDir),
    );
    const storedPath = yield* mapLocalDocumentError(
      'ingest',
      copySourceIntoStoreEffect(documentId, sourcePath, resolvedDataDir),
    );
    const now = new Date().toISOString();
    const extracted = yield* mapLocalDocumentError(
      'ingest',
      extractLocalDocumentEffect(storedPath, resolvedDataDir),
    );

    const pages = yield* Effect.forEach(extracted.pages, (page) =>
      mapLocalDocumentError(
        'ingest',
        writeDocumentPageTextEffect(documentId, page.pageNumber, page.text, resolvedDataDir),
      ).pipe(
        Effect.map((textPath) => ({
          pageNumber: page.pageNumber,
          textPath,
          charCount: page.text.length,
          excerpt: sanitizeExcerpt(page.text, 160),
          ocrApplied: page.ocrApplied,
        })),
      ),
    );

    const record: LocalDocumentRecord = {
      documentId,
      filename: basename(sourcePath),
      storedPath,
      sourcePath,
      status: extracted.charCount > 0 ? 'ready' : 'failed',
      pageCount: extracted.pageCount,
      charCount: extracted.charCount,
      createdAt: now,
      updatedAt: now,
      error: extracted.charCount > 0 ? null : 'No extractable text found in this PDF.',
      extractor: {
        ...extracted.tools,
        ocrUsed: extracted.ocrUsed,
      },
      pages: [...pages],
    };

    yield* mapLocalDocumentError('ingest', writeDocumentRecordEffect(record, resolvedDataDir));
    return {
      ...buildDocumentInfo(record),
      storedPath: record.storedPath,
    };
  });
}

export function ingestLocalDocument(sourcePath: string, dataDir?: string) {
  return runLocalDocumentEffect(ingestLocalDocumentEffect(sourcePath, dataDir));
}

export function getLocalDocumentStatusEffect(documentId: string, dataDir?: string) {
  return mapLocalDocumentError('status', readDocumentRecordEffect(documentId, dataDir)).pipe(
    Effect.map((record) => ({
      ...buildDocumentInfo(record),
      pagesWithText: record.pages.filter((page) => page.charCount > 0).length,
    })),
  );
}

export function getLocalDocumentStatus(documentId: string, dataDir?: string) {
  return runLocalDocumentEffect(getLocalDocumentStatusEffect(documentId, dataDir));
}

export function summarizeLocalDocumentEffect(documentId: string, dataDir?: string) {
  return Effect.gen(function* () {
    const record = yield* ensureReadyEffect('summary', documentId, dataDir);
    const { summary, citations } = yield* summarizeTextEffect(record, dataDir);
    return {
      ...buildDocumentInfo(record),
      summary,
      citations,
    };
  });
}

export function summarizeLocalDocument(documentId: string, dataDir?: string) {
  return runLocalDocumentEffect(summarizeLocalDocumentEffect(documentId, dataDir));
}

export function searchLocalDocumentEffect(documentId: string, query: string, dataDir?: string) {
  return Effect.gen(function* () {
    const record = yield* ensureReadyEffect('search', documentId, dataDir);
    const tokens = tokenizeQuery(query);
    const scored = yield* Effect.forEach(record.pages, (page) =>
      mapLocalDocumentError(
        'search',
        readDocumentPageTextEffect(record.documentId, page.pageNumber, dataDir),
      ).pipe(
        Effect.map((text) => {
          const lower = text.toLowerCase();
          let score = 0;
          for (const token of tokens) {
            const occurrences = lower.split(token).length - 1;
            score += occurrences * Math.max(1, token.length);
          }
          return {
            page: page.pageNumber,
            snippet: snippetAround(text, tokens),
            score,
          };
        }),
      ),
    );
    const matches = [...scored]
      .filter((match) => match.score > 0 || tokens.length === 0)
      .sort((lhs, rhs) => rhs.score - lhs.score)
      .slice(0, 5);

    return {
      ...buildDocumentInfo(record),
      query,
      matches,
    };
  });
}

export function searchLocalDocument(documentId: string, query: string, dataDir?: string) {
  return runLocalDocumentEffect(searchLocalDocumentEffect(documentId, query, dataDir));
}

export function readLocalPageEffect(documentId: string, pageNumber: number, dataDir?: string) {
  return Effect.gen(function* () {
    const record = yield* ensureReadyEffect('page', documentId, dataDir);
    const page = yield* localDocumentBoundary('page', () => {
      const found = record.pages.find((entry) => entry.pageNumber === pageNumber);
      if (!found) {
        throw new OkraRuntimeError(
          'DOCUMENT_NOT_FOUND',
          `Page ${pageNumber} not found for local document ${documentId}`,
          404,
        );
      }
      return found;
    });
    const text = yield* mapLocalDocumentError(
      'page',
      readDocumentPageTextEffect(documentId, pageNumber, dataDir),
    );
    return {
      ...buildDocumentInfo(record),
      page: page.pageNumber,
      text,
      ocrApplied: page.ocrApplied,
    };
  });
}

export function readLocalPage(documentId: string, pageNumber: number, dataDir?: string) {
  return runLocalDocumentEffect(readLocalPageEffect(documentId, pageNumber, dataDir));
}

function looksLikeTableRow(line: string): boolean {
  return /\S(?:.*(?: {2,}|\t).*)\S/.test(line.trim());
}

function detectTableCandidates(text: string, page: number, query?: string): LocalTableCandidate[] {
  const lines = text.split('\n');
  const tables: Array<{ rows: string[]; heading?: string }> = [];
  let current: string[] = [];
  let previousNonEmpty = '';

  for (const line of lines) {
    const trimmed = line.trim();
    if (looksLikeTableRow(line)) {
      current.push(line.trimEnd());
      continue;
    }

    if (current.length >= 2) {
      tables.push({ rows: current, heading: previousNonEmpty || undefined });
    }
    current = [];
    if (trimmed) {
      previousNonEmpty = trimmed;
    }
  }

  if (current.length >= 2) {
    tables.push({ rows: current, heading: previousNonEmpty || undefined });
  }

  const loweredQuery = query?.toLowerCase().trim();

  return tables
    .map((table) => {
      const body = table.rows.join('\n');
      const preview = sanitizeTablePreview(table.heading ? `${table.heading}\n${body}` : body, 320);
      const score = loweredQuery ? (preview.toLowerCase().includes(loweredQuery) ? 10 : 0) : 1;
      return {
        page,
        preview,
        rowCount: table.rows.length,
        score,
      };
    })
    .filter((table) => table.score > 0);
}

export function findLocalTablesEffect(documentId: string, query?: string, dataDir?: string) {
  return Effect.gen(function* () {
    const record = yield* ensureReadyEffect('tables', documentId, dataDir);
    const pageTables = yield* Effect.forEach(record.pages, (page) =>
      mapLocalDocumentError(
        'tables',
        readDocumentPageTextEffect(record.documentId, page.pageNumber, dataDir),
      ).pipe(Effect.map((text) => detectTableCandidates(text, page.pageNumber, query))),
    );
    const tables = pageTables
      .flatMap((candidates) => candidates)
      .sort((lhs, rhs) => (rhs.score ?? 0) - (lhs.score ?? 0))
      .slice(0, 8)
      .map(({ page, preview, rowCount }) => ({ page, preview, rowCount }));

    return {
      ...buildDocumentInfo(record),
      query,
      tables,
    };
  });
}

export function findLocalTables(documentId: string, query?: string, dataDir?: string) {
  return runLocalDocumentEffect(findLocalTablesEffect(documentId, query, dataDir));
}

export function doctorLocalHarnessEffect(
  dataDir?: string,
): Effect.Effect<LocalDoctorReport, LocalDocumentHarnessError> {
  const resolvedDataDir = resolveLocalDataDir(dataDir);
  return Effect.gen(function* () {
    yield* mapLocalDocumentError('doctor', ensureLocalStoreEffect(resolvedDataDir));
    return yield* mapLocalDocumentError('doctor', localToolReportEffect(resolvedDataDir));
  });
}

export function doctorLocalHarness(dataDir?: string): LocalDoctorReport {
  return runLocalDocumentEffect(doctorLocalHarnessEffect(dataDir));
}
